import Link from 'next/link';
import { useRouter } from 'next/router';
import { createStyles, Navbar } from '@mantine/core';
import { IconLogout, TablerIcon } from '@tabler/icons';

const useStyles = createStyles((theme, _params, getRef) => {
  const icon = getRef('icon');
  return {
    header: {
      paddingBottom: theme.spacing.md,
      marginBottom: theme.spacing.md * 1.5,
      borderBottom: `1px solid ${
        theme.colorScheme === 'dark'
          ? theme.colors.dark[4]
          : theme.colors.gray[2]
      }`,
    },

    footer: {
      paddingTop: theme.spacing.md,
      marginTop: theme.spacing.md,
      borderTop: `1px solid ${
        theme.colorScheme === 'dark'
          ? theme.colors.dark[4]
          : theme.colors.gray[2]
      }`,
    },

    link: {
      ...theme.fn.focusStyles(),
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
      fontSize: theme.fontSizes.sm,
      color:
        theme.colorScheme === 'dark'
          ? theme.colors.dark[1]
          : theme.colors.gray[7],
      padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
      borderRadius: theme.radius.sm,
      fontWeight: 500,

      '&:hover': {
        backgroundColor:
          theme.colorScheme === 'dark'
            ? theme.colors.dark[6]
            : theme.colors.gray[0],
        color: theme.colorScheme === 'dark' ? theme.white : theme.black,

        [`& .${icon}`]: {
          color: theme.colorScheme === 'dark' ? theme.white : theme.black,
        },
      },
    },

    linkIcon: {
      ref: icon,
      color:
        theme.colorScheme === 'dark'
          ? theme.colors.dark[2]
          : theme.colors.gray[6],
      marginRight: theme.spacing.sm,
    },

    linkActive: {
      '&, &:hover': {
        backgroundColor: theme.fn.variant({
          variant: 'light',
          color: theme.primaryColor,
        }).background,
        color: theme.fn.variant({ variant: 'light', color: theme.primaryColor })
          .color,
        [`& .${icon}`]: {
          color: theme.fn.variant({
            variant: 'light',
            color: theme.primaryColor,
          }).color,
        },
      },
    },
  };
});

export interface SideNavProps {
  data: Array<{ link: string; label: string; icon: TablerIcon }>;
  opened?: boolean;
}

const SideNav = ({ data, opened }: SideNavProps): JSX.Element => {
  const { classes, cx } = useStyles();
  const router = useRouter();

  const links = data.map((item) => {
    const active = router.pathname.startsWith(item.link);
    return (
      <Link key={item.label} href={item.link} passHref>
        <a
          className={cx(classes.link, {
            [classes.linkActive]: active,
          })}
        >
          <item.icon className={classes.linkIcon} stroke={1.5} />
          <span>{item.label}</span>
        </a>
      </Link>
    );
  });

  return (
    <Navbar width={{ sm: 300 }} p="md" hidden={!opened}>
      <Navbar.Section grow>{links}</Navbar.Section>

      <Navbar.Section className={classes.footer}>
        <Link href="/admin/logout" passHref>
          <a className={classes.link}>
            <IconLogout className={classes.linkIcon} stroke={1.5} />
            <span>ログアウト</span>
          </a>
        </Link>
      </Navbar.Section>
    </Navbar>
  );
};

export default SideNav;
